/**
 * THE CADENCE FORECAST, PER MILESTONE — the portfolio read's enrichment step
 * (project-milestone-forecast-from-cadence). It joins the pure milestone set
 * (src/milestones.ts) to the forecasting engine (src/insights.ts): for each milestone
 * that gates real work, it runs the board's own throughput against that milestone's
 * open cards and attaches the resulting {@link Forecast} to the milestone, so the
 * roll-up cell, the drill and the milestone table all read `m.forecast` and never
 * recompute. Kept out of milestones.ts so the pure derivation stays free of the
 * Monte-Carlo, and out of insights.ts so the engine stays free of milestone shapes.
 */
import type { Issue } from './types';
import type { Milestone } from './milestones';
import { milestoneScope } from './milestones';
import { forecastScoped, type Forecast } from './insights';
import type { WorkCalendar } from './work-calendar';

/** A plain `YYYY-MM-DD` as its LOCAL midnight instant — the forecast target, so
 *  `forecast.targetProb` reads the chance the PLAN date is met. Local midnight (not
 *  UTC) is the app's convention for a plain plan day (see ProjectStatus `planMsOf`,
 *  `dayParts`); parsing it as UTC made the forecast band's Go-Live marker render a day
 *  early west of UTC (e.g. "14 Jan" for a 15 Jan plan in UTC-4) while the plan target
 *  beside it, read straight off the ISO string, correctly said 15 Jan. */
const planMs = (iso?: string): number | undefined => {
  if (!iso) return undefined;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d).getTime();
};

/** Trials for the ROLL-UP glance — fewer, for a snappy grid, still deterministic and
 *  plenty for a stable p50/p85 headline. The drill and evidence use the engine's full
 *  default (10,000) where the whole spread is drawn. */
export const FORECAST_TRIALS_GLANCE = 3000;

/**
 * ONE MILESTONE'S CADENCE FORECAST, or `undefined` when there is nothing to predict:
 * the milestone is already done, or it has no scope (a standalone with no tie — it
 * keeps reading "from the plan"). A milestone WITH a scope but too little board history
 * still returns a Forecast — one whose `ready` is false — so the caller can show the
 * honest "not enough history yet" state rather than a silent gap. The target is the
 * milestone's planned day; the seed folds in the board and milestone key so the date is
 * reproducible and two checkpoints on one board never share a seed.
 */
export function milestoneForecast(
  m: Milestone,
  boardIssues: Issue[],
  now: number,
  trials?: number,
  cal?: WorkCalendar,
): Forecast | undefined {
  if (m.actualDay) return undefined; // already landed — not a forecast
  const scope = milestoneScope(m, boardIssues);
  if (scope === null) return undefined; // no tie — reads from the plan
  return forecastScoped(boardIssues, scope, now, planMs(m.plannedDay), 12, trials, undefined, `${m.boardId}|${m.key}`, cal);
}

/**
 * ATTACH a cadence forecast to each milestone in a set that has one, leaving the
 * others untouched. The cells read `m.forecast`. Pass the glance trial count for the
 * roll-up; omit it for the drill/evidence (full accuracy).
 *
 * SEQUENTIAL / CUMULATIVE SCOPE (29 Oct). A board clears one checkpoint's work before
 * the next, so a milestone is predicted from the pace clearing EVERYTHING due up to and
 * including it — its own open cards UNION every earlier milestone's. The first cut gave
 * each milestone ITS OWN scope alone, which silently assumed the whole team's throughput
 * on every checkpoint at once: the parts came out optimistic and never summed to the
 * whole-board go-live (four milestones each "done by mid-Nov" on a board that finishes in
 * June). Accumulating the scope in due order makes each predicted date land after the one
 * before it, and the terminal go-live — tied to the whole board — has the whole board as
 * its cumulative scope, so the parts now add up to it. A milestone with no tie gates no
 * work and is skipped (it keeps reading "from the plan"); a landed milestone still joins
 * the run it was part of (its cards are already closed, so they add nothing) but is not
 * itself re-forecast. Order is by plan day (fallback forecast day), ties by position.
 */
export function withMilestoneForecasts(
  set: Milestone[],
  boardIssues: Issue[],
  now: number,
  trials?: number,
  cal?: WorkCalendar,
): Milestone[] {
  const ms = (iso?: string): number => (iso ? Date.parse(`${iso}T00:00:00Z`) : Number.POSITIVE_INFINITY);
  const ordered = set
    .map((m, i) => ({ m, i }))
    .sort((a, b) => {
      const da = ms(a.m.plannedDay ?? a.m.forecastDay);
      const db = ms(b.m.plannedDay ?? b.m.forecastDay);
      return da === db ? a.i - b.i : da - db;
    });

  const cumulative = new Set<string>(); // ids of the cards due by the current checkpoint
  const byKey = new Map<string, Forecast>();
  for (const { m } of ordered) {
    const scope = milestoneScope(m, boardIssues);
    if (scope === null) continue; // no tie — reads from the plan, gates nothing
    for (const i of scope) cumulative.add(i.id); // this checkpoint's work joins the run
    if (m.actualDay) continue; // already landed — not a forecast
    const cumScope = boardIssues.filter((i) => cumulative.has(i.id));
    byKey.set(
      m.key,
      forecastScoped(boardIssues, cumScope, now, planMs(m.plannedDay), 12, trials, undefined, `${m.boardId}|${m.key}`, cal),
    );
  }
  return set.map((m) => {
    const f = byKey.get(m.key);
    return f ? { ...m, forecast: f } : m;
  });
}
